<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { RBtn } from '@therobotcompany/robco-frontend-library';
import gettext from '@/i18n/gettext';
import type { CatalogueItem } from '@/models/Catalogue';
import { useCataloguePermissions } from '@/composables/catalogue/useCataloguePermissions';
import CatalogueImageViewer from '@/components/catalogue/CatalogueImageViewer.vue';
import { CatalogueItemActionsKey } from '@/composables/catalogue/useCatalogueItemActions';

const { $gettext } = gettext;
const { canEditItem } = useCataloguePermissions();
const actions = inject(CatalogueItemActionsKey)!;

const props = defineProps<{
  item: CatalogueItem;
  images: string[];
}>();

const canEdit = computed(() => {
  return canEditItem(props.item);
});

const showDeleteDialog = ref(false);
const imageToDelete = ref<string>('');
const showImageViewer = ref(false);
const selectedImage = ref<string>('');

const handleImageDelete = (imageUrl: string) => {
  imageToDelete.value = imageUrl;
  showDeleteDialog.value = true;
};

const confirmImageDelete = () => {
  const filename = imageToDelete.value.split('/').pop();
  if (filename) {
    actions.deleteImage(filename);
  }
  showDeleteDialog.value = false;
  imageToDelete.value = '';
};

const cancelImageDelete = () => {
  showDeleteDialog.value = false;
  imageToDelete.value = '';
};

const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    actions.uploadImage(target.files[0]);
  }
};

const openImageViewer = (imageUrl: string) => {
  selectedImage.value = imageUrl;
  showImageViewer.value = true;
};
</script>

<template>
  <div>
    <div class="d-flex align-center mb-4">
      <h3 class="text-h6">{{ $gettext('Images') }}</h3>
      <v-spacer />
      <r-btn
        v-if="canEdit"
        icon="mdi-upload"
        variant="outlined"
        @click="($refs.imageInput as HTMLInputElement).click()"
      >
        {{ $gettext('Upload Image') }}
      </r-btn>
      <v-file-input
        v-show="false"
        ref="imageInput"
        accept="image/*"
        @change="handleFileUpload"
      />
    </div>

    <v-row v-if="images.length > 0">
      <v-col v-for="image in images" :key="image" cols="12" sm="6" md="4">
        <div class="position-relative image-container">
          <v-img
            :src="image"
            :alt="(image.split('/').pop() || 'catalogue image').split('?')[0]"
            aspect-ratio="1"
            cover
            class="rounded cursor-pointer"
            @click="openImageViewer(image)"
          />
          <v-icon
            v-if="canEdit"
            icon="mdi-delete"
            color="error"
            class="position-absolute top-0 end-0 ma-2 cursor-pointer delete-icon"
            @click.stop="handleImageDelete(image)"
          />
          <div
            class="image-overlay cursor-pointer"
            @click="openImageViewer(image)"
          >
            <v-icon icon="mdi-magnify" color="white" size="24" />
          </div>
        </div>
      </v-col>
    </v-row>

    <v-alert
      v-else
      type="info"
      variant="tonal"
      :text="$gettext('No images uploaded yet')"
    />

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>{{ $gettext('Delete Image') }}</v-card-title>
        <v-card-text>
          {{
            $gettext(
              'Are you sure you want to delete this image? This action cannot be undone.',
            )
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey-darken-1"
            variant="text"
            @click="cancelImageDelete"
          >
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-btn color="error" variant="text" @click="confirmImageDelete">
            {{ $gettext('Delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Image Viewer -->
    <CatalogueImageViewer
      v-model="showImageViewer"
      :image-url="selectedImage"
    />
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}

.top-0 {
  top: 0;
}

.end-0 {
  right: 0;
}

.ma-2 {
  margin: 8px;
}

.image-container {
  transition: transform 0.2s ease;
}

.image-container:hover {
  transform: scale(1.02);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  border-radius: 4px;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.delete-icon {
  z-index: 10;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  padding: 4px;
}
</style>
